import React, { useEffect, useState } from 'react';

import {TableRow, TableCell, Table, TableBody, IconButton, CircularProgress} from "@material-ui/core";
import axios from "axios";
import config from '../config'
import fileDownload from 'js-file-download';
import GetAppIcon from '@material-ui/icons/GetApp';
import _ from 'lodash'
import { getFileDescription } from "../actions/FlowableFormActions";
import {FileDetails, FlowableFormField, FormState} from "../types/FlowableFormTypes";
import { ProcessInstanceQueryResult } from "../types/FlowableQueryResultsTypes"
import {format, parse} from "date-fns";
import {formatPhoneNumbers} from "../utils/formatUtils";
import {EditField} from "./EditField";
import {AccountInfo} from "../types/UserInfoTypes";
import ReactDOMServer from 'react-dom/server';
import Alert from "@material-ui/lab/Alert";

interface SummaryTableProps {
    processInstanceId:string,
    showComparison: boolean,
    expression:string | undefined,
    priorityFields?:Record<string, unknown> | undefined,
    onLoadTableFields?:(fields:FlowableFormField[])=>void
    accountInfo?: AccountInfo
}

const editableAgreementFields: string[] = [
    'documentationtype',
    'atdexpirationdate',
    'applicationsystemidentifier',
    'applicationsystemidentifiertype',
    'emassidentifier',
    'sorn'
];

const previousAgreementBusinessKey = (businessKey: string): string => {
    const businessKeyParts = businessKey.split("-");
    const previousAgreementNumber = parseInt(businessKeyParts.pop() ?? '0') - 1;
    if (previousAgreementNumber >= 0) {
        const formattedPreviousAgreementNumber = previousAgreementNumber < 10 ? `0${previousAgreementNumber}` : previousAgreementNumber.toString();
        return `${businessKeyParts.join('-')}-${formattedPreviousAgreementNumber}`;
    }
    return '';
}

const isSecurityTeam = (accountInfo: AccountInfo | undefined): boolean => (
    accountInfo !== undefined && accountInfo.groups.find((group) => group.id === 'IASupportTeam') !== undefined
);

const getFieldCellValue = (field: FlowableFormField, renderFields: FlowableFormField[]) => {
    if (field.value && field.id !== 'deniedreason' && field.type !== "expression") {
        let cellValue = field.value
        if (field.type === 'upload') {
            const tableRows = []
            const fileIds = (cellValue as string).split(",")
            for (const fileId of fileIds) {
                tableRows.push(
                    <TableRow key={`file-id-${fileId}`}>
                        <FileDetailsSummary fileId={fileId}></FileDetailsSummary>
                    </TableRow>
                )
            }
            cellValue = (
                <Table><TableBody>{tableRows}</TableBody></Table>
            )
        }
        if (field.type === "date") {
            cellValue = (
                <>{format(parse(field.value as string, "yyyy-M-d", new Date()), "MM/dd/yy")}</>
            )
        } else if (field.type === "people") {
            const values = JSON.parse(field.value as string)
            cellValue = (
                <div>
                    <div>ID: {values.id}</div>
                    <div>Name: {values.fullName}</div>
                    {values.email ? <div>Email: {values.email}</div> : <span></span>}
                    {values.attributes ?
                        <div>Phone: {formatPhoneNumbers(values.attributes["phone_number"][0] as string)}</div> :
                        <span></span>}
                    {values.usertelephonenumber ?
                        <div>Phone: {formatPhoneNumbers(values.usertelephonenumber as string)}</div> : <span></span>}
                </div>
            )
        } else if (field.type === 'summary-section' || field.name === 'Label') {
            cellValue = null
        } else if (field.type === 'modal-section') {
            if (field.id === 'newusers') { //skip this modal since it's designed for write-only
                return null;
            }
            // skip the new users modal as it's write only
            const values = JSON.parse(field.value as string)
            const tableRows = []
            for (const value of (values as Array<{ [key: string]: unknown }>)) {
                const tableCells = []
                for (const key of Object.keys(value)) {
                    const fieldDefinition = renderFields.find(f => f.id == key) as FlowableFormField
                    if (fieldDefinition) {
                        const fieldValue = []
                        if (fieldDefinition.type === 'upload') {
                            const fileIds = (value[key] as string).split(",")
                            for (const fileId of fileIds) {
                                fieldValue.push(<FileDetailsSummary key={`file-details-${fileId}`} fileId={fileId}/>)
                            }
                        } else {
                            fieldValue.push(value[key])
                        }
                        tableCells.push(
                            <div
                                key={`field-definition-${fieldDefinition.name}`}>{fieldDefinition.name}: {fieldValue}</div>
                        )
                    } else {
                        console.log(`Unable to find field definition for: ${key}`);
                    }

                }
                tableRows.push(
                    <TableRow><TableCell>
                        {tableCells}
                    </TableCell></TableRow>
                )
            }
            cellValue = (
                <Table><TableBody>{tableRows}</TableBody></Table>
            )
        } else if (field.params && field.params.multiselect) {
            const values = JSON.parse(field.value as string)
            const rows = []
            for (const value of values) {
                rows.push(<div key={`field-value-${value}`}>{value}</div>)
            }
            cellValue = (
                <span>{rows}</span>
            )
        }

        return cellValue;

    }


};

// eslint-disable-next-line react/display-name
export const SummaryTable = React.forwardRef<HTMLInputElement, SummaryTableProps>((props: SummaryTableProps, ref) => {
  const [tableFields, setTableFields] = useState<FlowableFormField[]>([]);
  const [applications, setApplications] = useState<FlowableFormField[][]>([]);
  const [previousAgreementTableFields, setPreviousAgreementTableFields] = useState<FlowableFormField[]>([]);
  const [previousapplications, setPreviousApplications] = useState<FlowableFormField[][]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const manualApplicationReview: boolean = props.showComparison && previousapplications.length > 0 && applications.length !== previousapplications.length;

    async function loadData(): Promise<void>{
        try{
            setLoading(true);
            props.onLoadTableFields && props.onLoadTableFields([])

            const processInstanceResponse = (await axios.get(
                `${config.flowableUiApiBaseUrl}app/rest/process-instances/${props.processInstanceId}?includeProcessVariables=true`,
                {
                    withCredentials:true
                })).data;

            const [newTableFields, businessKey] = await getTableFields(processInstanceResponse, props.priorityFields);
            // Append the Request Number at the front of the fields taking the form of a form field.
            newTableFields.unshift({
                ...newTableFields[0],
                id: "businessKey",
                name: "Request Number",
                value: businessKey,
                type: "text",
            })

            const [applications] = await getApplications(processInstanceResponse);

            setTableFields(newTableFields);
            setApplications(applications);

            const previousBusinessKey = previousAgreementBusinessKey(businessKey);

            if (previousBusinessKey && props.showComparison) {

                const previousAgreementProcessInstanceResponse = (await axios.post(
                    `${config.flowableUiApiBaseUrl}app/rest/query/process-instances/previous-agreement`,
                    {
                        businessKey
                    },
                    {
                        withCredentials:true
                    })).data;

                if (previousAgreementProcessInstanceResponse && previousAgreementProcessInstanceResponse.data && previousAgreementProcessInstanceResponse.data.length > 0) {
                    const [newTableFields] = await getTableFields(previousAgreementProcessInstanceResponse.data[0], props.priorityFields);
                    // Append the Request Number at the front of the fields taking the form of a form field.
                    newTableFields.unshift({
                        ...newTableFields[0],
                        id: "businessKey",
                        name: "Request Number",
                        value: previousBusinessKey,
                        type: "text",
                    })

                    const [applications] = await getApplications(previousAgreementProcessInstanceResponse.data[0]);

                    setPreviousAgreementTableFields(newTableFields);
                    setPreviousApplications(applications);
                }


            }

            props.onLoadTableFields && props.onLoadTableFields(newTableFields)
            setLoading(false);
        } catch(e){
            console.error(e);
            setTableFields([])
            props.onLoadTableFields && props.onLoadTableFields([])
        }
    }

  useEffect(()=>{
      loadData()
  }, [props.processInstanceId])

    const processField = (field: FlowableFormField, renderFields: FlowableFormField[], applications?: FlowableFormField[][], applicationIndex?: number): null | JSX.Element => {

        const formState: FormState = renderFields.reduce((accumulator, currentValue) => {
            return {...accumulator, ...{[currentValue.id] : currentValue.value}};
        })

        const cellValue = getFieldCellValue(field, renderFields);

        const getPreviousValue = (): (string | JSX.Element | unknown) => {
            if (applicationIndex !== undefined) {
                if (!manualApplicationReview && previousapplications.length > 0 && applications && applications.length > 0) {
                    if (previousapplications[applicationIndex]) {
                        const applicationFields = _.uniqBy(previousapplications[applicationIndex], 'id');
                        const previousValue = applicationFields.find((f) => f.id === field.id);
                        return previousAgreementTableFields.length > 0 ? getFieldCellValue({...field, ...{value: previousValue ? previousValue.value : ''}}, applicationFields) : '';
                    } else {
                        return '';
                    }
                }
            } else if (previousAgreementTableFields.length > 0) {
                const previousTableValues = _.uniqBy(previousAgreementTableFields, 'id');
                const previousValue = previousTableValues.find((f) => f.id === field.id);
                return previousAgreementTableFields.length > 0 ? getFieldCellValue({...field, ...{value: previousValue ? previousValue.value : ''}}, previousTableValues) : '';
            }

            return '';
        };

        const previousCellValue = props.showComparison ? getPreviousValue() : null;

        if ((cellValue || previousCellValue) && field.id !== 'agreement') {

            return (
                <TableRow key={field.id as string}>
                    <TableCell style={{width: '60%'}}>{field.name}</TableCell>
                    {previousAgreementTableFields.length > 0 ? <TableCell>{(previousCellValue ?? '-') as string | JSX.Element}</TableCell> : null}

                    <TableCell style={{whiteSpace: 'pre-wrap', wordWrap: 'break-word', backgroundColor: previousAgreementTableFields.length > 0 && ReactDOMServer.renderToString(cellValue as JSX.Element) !== ReactDOMServer.renderToString(previousCellValue as JSX.Element) ? 'yellow' : 'white'}}>{cellValue as string | JSX.Element} {editableAgreementFields.includes(field.id) && isSecurityTeam(props.accountInfo) ? <EditField field={field} fields={renderFields} formState={formState} processInstanceId={props.processInstanceId} loadData={loadData} applications={applications} applicationIndex={applicationIndex} /> : null}</TableCell>
                </TableRow>
            );
        }

        return null;
    };

    const renderFields = _.uniqBy(tableFields, 'id');

  const table: JSX.Element[] = renderFields.reduce((accumulator: JSX.Element[], field) => {
      const processedField: JSX.Element | null = processField(field, renderFields);
      if (processedField) {
          return [...accumulator, processedField];
      }
      return accumulator;
  }, []);
  
    const renderApplications = () => {
        if (applications.length === 0 || props.expression !== 'variables') {
            return null;
        }

        const applicationData = applications.map((application: FlowableFormField[], applicationIndex: number) => {
            const fieldRows: (JSX.Element | null)[] = application.map((field: FlowableFormField) => {
                return processField(field, application, applications, applicationIndex);
            });
            return <React.Fragment key={`application-${applicationIndex}`}><h4>{`Application #${applicationIndex+1}`}</h4><Table><TableBody>{fieldRows}</TableBody></Table></React.Fragment>
        });

        const renderManualNotification = () => {
            if (manualApplicationReview) {
                return <Alert severity="error">The number of applications has changed in this renewal. Please review the changes manually.</Alert>;
            }
            return null;
        }

        return <><h2>Applications</h2>{renderManualNotification()}{applicationData}</>
    };

    const tableElem = !loading ? <Table><TableBody>{table}</TableBody></Table> : <CircularProgress />
    return <div className={'fieldDiv'} ref={ref}>{tableElem}{renderApplications()}</div>
  });

function FileDetailsSummary(props:{fileId: string}) {
  const [fileDetail, setFileDetail] = useState<FileDetails|null>(null)
  useEffect(()=>{
    async function getFileDetails(){
      setFileDetail(await getFileDescription(props.fileId))
    }
    getFileDetails()
  }, [props.fileId])

  return fileDetail ? <><TableCell>
    <div>Name: {fileDetail.name}</div>
    {/* <div>Size: {props.fileDetail.contentSize}</div> */}
  </TableCell>
  <TableCell>
  <IconButton style={{float:"right"}} edge="end" onClick={()=>{
    axios.get(`${config.flowableUiApiBaseUrl}app/rest/content/${fileDetail.id}/raw`, {withCredentials:true, responseType:'blob'})
      .then(res => fileDownload(res.data, fileDetail.name as string))
  }}>
    <GetAppIcon />
  </IconButton>
  </TableCell></>:<></>
}

/**
 * Pull process instance variables & combine with values entered in the Task Forms for each task in the process.
 * IF only Task Form values are used, they are typically out of date with the updated process.
 * IF `priorityFields` are specified, ONLY those fields will be rendered.
 *
 * @param processInstanceResponse process instance info
 * @param priorityFields an object containing the specific fields to render for the particular summary.
 */
// eslint-disable-next-line
async function getTableFields(processInstanceResponse: any, priorityFields:Record<string, unknown> | undefined){
    const {id, processVariables, businessKey} = processInstanceResponse;

    const taskResponse = (await axios.post(`${config.flowableUiApiBaseUrl}app/rest/query/tasks`, {
        processInstanceIdsIn: [id].join(','),
      size:1000000,
      sort: "created-asc",
      state: "completed",
        includeProcessInstance: true
    }, { withCredentials: true })).data.data

    // get the last task for each task definition to avoid duplication
    const latestTaskForTaskDefinition = _.values(_.keyBy(taskResponse, tr => tr.taskDefinitionKey))
    const ids = latestTaskForTaskDefinition.map((data: { id: string; }) => data.id) as string[]


    // TODO: Refactor to be a bulk request on the API side instead of X number of GET calls
    const getTaskForms = ids.map(id => {
      return axios.get(`${config.flowableUiApiBaseUrl}app/rest/task-forms/${id}`, { withCredentials: true })
    })

    const taskFormFields = (await Promise.all(getTaskForms)).map(resp=> {
      return resp.data.fields
    }) as FlowableFormField[]

    let fieldsToMap: FlowableFormField[];
    if(!priorityFields || (priorityFields && processVariables && processVariables.variables && Object.keys(priorityFields).length === Object.keys(processVariables.variables).length)) {
        fieldsToMap = _.flatMap(taskFormFields);
    } else {
        fieldsToMap = _.flatMap(taskFormFields).filter(f => _.includes(Object.keys(priorityFields), f.id));
    }
    const mapped = fieldsToMap.map( (f, i, a) => {
      if(!(null === processVariables[f.id] || undefined === processVariables[f.id])) {
        a[i].value = processVariables[f.id];
      }
      return a[i];
    }) as FlowableFormField[];

    return [mapped, businessKey];
  }

// eslint-disable-next-line
async function getApplications(processInstanceResponse: any){

    const {id, processVariables} = processInstanceResponse;

    let applicationList = processVariables.applications;

    const subProcesses: ProcessInstanceQueryResult[] = (await axios.post(`${config.flowableUiApiBaseUrl}app/rest/query/process-instances`, {
        superProcessInstanceId: id,
        size:1000000,
        sort: "created-asc",
        state: "all",
        includeProcessInstance: true
    }, { withCredentials: true })).data.data

    if (applicationList && applicationList.length === 0 && subProcesses.length > 0) {
        const subProcessResponse = (await axios.get(
            `${config.flowableUiApiBaseUrl}app/rest/process-instances/${subProcesses[0].id}?includeProcessVariables=true`,
            {
                withCredentials:true
            })).data;

        if (subProcessResponse.processVariables && subProcessResponse.processVariables.applications && subProcessResponse.processVariables.applications.length > 0) {
            applicationList = subProcessResponse.processVariables.applications;
        }
    }

    const subProcessIds = subProcesses.map((subProcess: ProcessInstanceQueryResult) => {
        return subProcess.id;
    });

    const taskResponse = (await axios.post(`${config.flowableUiApiBaseUrl}app/rest/query/tasks`, {
        processInstanceIdsIn: [...subProcessIds, id].join(','),
        size:1000000,
        sort: "created-asc",
        state: "completed",
        includeProcessInstance: true
    }, { withCredentials: true })).data.data

    // get the last task for each task definition to avoid duplication
    const latestTaskForTaskDefinition = _.values(_.keyBy(taskResponse, tr => tr.taskDefinitionKey))
    const ids: string[] = latestTaskForTaskDefinition.map((data: { id: string; }) => data.id) as string[]

    // TODO: Refactor to be a bulk request on the API side instead of X number of GET calls
    const getTaskForms = ids.map(id => {
        return axios.get(`${config.flowableUiApiBaseUrl}app/rest/task-forms/${id}`, { withCredentials: true })
    })

    const taskFormFields = (await Promise.all(getTaskForms)).map(resp=> {
        return resp.data.fields
    }) as FlowableFormField[]

    const fieldsToMap: FlowableFormField[] = _.flatMap(taskFormFields);

    // eslint-disable-next-line
    const applications: FlowableFormField[][] = applicationList ? applicationList.map((application: any) => {
        return Object.keys(application).reduce((applicationFields: FlowableFormField[], fieldKey: string): FlowableFormField[] => {
            const field = fieldsToMap.find(mapField => fieldKey === mapField.id);
            if (field) {
                return [...applicationFields, {...field, ...{value: application[fieldKey]}}];
            }
            return applicationFields;
        }, []);
    }) : [];

    return [applications];
}
